"use client";

import React, { useCallback, useMemo, useState } from "react";
import {
  Card,
  Button as ButtonComponent,
  Modal,
  AlertModal,
  Input,
  Textarea,
  Switch,
} from "@/components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { notificationsInactiveSchema } from "@/validations";
import { renderTemplate } from "@/utils/templateRenderer";
import { Plus, Pencil, Trash, Eye, Send } from "lucide-react";
import useNotificationsInactive from "@/hooks/notificationsInactive/useNotificationsInactive";
import axiosInstance from "@/utils/axiosInstance";
import { useMe } from "@/hooks";
import type { InactiveNotificationTemplate } from "@/types/notifications";
import { toast } from "react-toastify";

type Props = { dict: any };

const NotificationsInactiveManager: React.FC<Props> = ({ dict }) => {
  const {
    templates,
    loading,
    refresh,
    createTemplate,
    updateTemplate,
    removeTemplate,
  } = useNotificationsInactive(() => refresh());
  const { user: meUser } = useMe();

  // load templates on mount
  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const [editing, setEditing] = useState<InactiveNotificationTemplate | null>(
    null,
  );
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [toDeleteId, setToDeleteId] = useState<number | null>(null);
  const [preview, setPreview] = useState<{
    title?: string;
    withSub?: string;
    withoutSub?: string;
  } | null>(null);
  const [openForm, setOpenForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    resolver: yupResolver(notificationsInactiveSchema as any),
    defaultValues: {
      title: "",
      inactive_days: 30,
      message_with_subscription: "",
      message_without_subscription: "",
      active: true,
    },
  });

  const openCreate = useCallback(() => {
    setEditing(null);
    reset({
      title: "",
      inactive_days: 30,
      message_with_subscription: "",
      message_without_subscription: "",
      active: true,
    });
    setOpenForm(true);
  }, [reset]);

  const openEdit = useCallback(
    (tpl: any) => {
      setEditing(tpl);
      reset({ ...tpl });
      setOpenForm(true);
    },
    [reset],
  );

  const onSubmit = useCallback(
    async (data: any) => {
      if (editing && editing.id) {
        await updateTemplate({ id: editing.id, ...data });
      } else {
        await createTemplate(data);
      }
      reset();
      setOpenForm(false);
    },
    [editing, createTemplate, updateTemplate, reset],
  );

  const handleDelete = useCallback((tpl: any) => {
    setToDeleteId(tpl.id);
    setOpenDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (toDeleteId != null) await removeTemplate(toDeleteId);
    setToDeleteId(null);
    setOpenDeleteConfirm(false);
  }, [toDeleteId, removeTemplate]);

  const handleToggleActive = useCallback(
    async (tpl: InactiveNotificationTemplate) => {
      await updateTemplate({
        id: tpl.id as number,
        title: tpl.title,
        inactive_days: tpl.inactive_days,
        message_with_subscription: tpl.message_with_subscription,
        message_without_subscription: tpl.message_without_subscription,
        active: !tpl.active,
      });
    },
    [updateTemplate],
  );

  const handlePreview = useCallback((tpl: any) => {
    const example = { nome: "Ana", servico: "Corte", dias: 31 };
    setPreview({
      title: tpl?.title,
      withSub: renderTemplate(tpl?.message_with_subscription || "", example),
      withoutSub: renderTemplate(
        tpl?.message_without_subscription || "",
        example,
      ),
    });
  }, []);

  const handleSendTest = useCallback(
    async (body: string, title?: string) => {
      const notifiableId = (meUser as any)?.id;

      if (!notifiableId) {
        toast.error(
          dict?.["Failed to send notification"] ||
            "Failed to send notification",
        );
        return;
      }

      try {
        const { data } = await axiosInstance.post("/send-notification", {
          notifiable_id: notifiableId,
          title: title || dict?.["Inactive notification"] || "Inatividade",
          body,
        });

        const successMessage =
          data?.message ||
          dict?.["Notification sent successfully"] ||
          "Notification sent successfully";
        toast.success(successMessage);
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          dict?.["Failed to send notification"] ||
          "Failed to send notification";
        toast.error(errorMessage);
      }
    },
    [meUser, dict],
  );

  const messageWithError = (() => {
    const m = (errors as any)?.message_with_subscription;
    if (!m) return null;
    if (typeof m === "string") return m;
    if (typeof m?.message === "string") return m.message;
    return String(m);
  })();

  const rows = useMemo(() => templates || [], [templates]);

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <ButtonComponent
          onClick={openCreate}
          icon={<Plus />}
          text={dict["New Template"] || "New Template"}
          variant="secondary"
        />
      </div>

      <div className="w-full overflow-auto">
        <table className="w-full table-auto text-sm">
          <thead>
            <tr className="text-left">
              <th className="p-2">{dict["Template Title"] || "Title"}</th>
              <th className="p-2">
                {dict["Inactive days"] || "Inactive days"}
              </th>
              <th className="p-2">{dict.Active || "Active"}</th>
              <th className="p-2">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="p-6 text-center text-muted-foreground"
                >
                  {dict["No templates"] || "No templates found."}
                </td>
              </tr>
            ) : (
              rows.map((tpl: any) => (
                <tr key={tpl.id} className="border-t">
                  <td className="p-2 align-top">
                    {tpl.title ||
                      (tpl.message_with_subscription || "").slice(0, 20)}
                  </td>
                  <td className="p-2 align-top">{tpl.inactive_days}</td>
                  <td className="p-2 align-top">
                    <Switch
                      checked={!!tpl.active}
                      onChange={() => handleToggleActive(tpl)}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex gap-2">
                      <button
                        className="btn btn-ghost"
                        onClick={() => handlePreview(tpl)}
                        title={dict.Preview}
                      >
                        <Eye size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => openEdit(tpl)}
                        title={dict.Edit}
                      >
                        <Pencil size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost text-destructive"
                        onClick={() => handleDelete(tpl)}
                        title={dict["Delete template?"]}
                      >
                        <Trash size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() =>
                          handleSendTest(
                            tpl.message_with_subscription,
                            tpl.title,
                          )
                        }
                        title={dict["Send test"] || "Send test"}
                        aria-label={dict["Send test"] || "Send test"}
                      >
                        <Send
                          size={18}
                          className="text-green-500 cursor-pointer"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        titleModal={
          editing ? dict.Edit || "Edit" : dict["New Template"] || "New Template"
        }
        open={openForm}
        onOpenChange={setOpenForm}
      >
        <Card>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4">
              <div>
                <label className="block text-sm mb-1">
                  {dict["Inactive days"] || "Inactive days"}
                </label>
                <Input
                  type="number"
                  {...register("inactive_days", { valueAsNumber: true })}
                />
                <div className="text-xs text-destructive mt-1">
                  {(errors as any)?.inactive_days?.message || null}
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">
                  {dict["Message with subscription"] ||
                    "Message with subscription"}
                </label>
                <Textarea
                  {...register("message_with_subscription")}
                  maxLength={500}
                  rows={3}
                />
                <div className="text-xs text-muted-foreground border rounded bg-muted/30 p-3">
                  {dict["Inactive placeholders help"]}
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">
                  {dict["Message without subscription"] ||
                    "Message without subscription"}
                </label>
                <Textarea
                  {...register("message_without_subscription")}
                  maxLength={500}
                  rows={3}
                />
                <div className="text-xs text-destructive mt-1">
                  {(errors as any)?.message_without_subscription?.message ||
                    null}
                </div>
                <div className="text-xs text-muted-foreground border rounded bg-muted/30 p-3">
                  {dict["Inactive placeholders help"]}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="block text-sm">
                  {dict.Active || "Active"}
                </label>
                <Switch
                  checked={!!watch("active")}
                  onChange={(v) => setValue("active", v)}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <ButtonComponent
                  text={dict.Cancel || "Cancel"}
                  variant="default"
                  onClick={() => {
                    reset();
                    setOpenForm(false);
                  }}
                  type="button"
                />
                <ButtonComponent
                  text={dict.Save || "Save"}
                  variant="success"
                  type="submit"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </form>
        </Card>
      </Modal>

      <Modal
        titleModal={dict.Preview || "Preview"}
        open={!!preview}
        onOpenChange={() => setPreview(null)}
      >
        <Card>
          <div className="space-y-3">
            {preview?.title ? (
              <h4 className="text-base font-semibold">{preview.title}</h4>
            ) : null}
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {dict["Message with subscription"] ||
                  "Message with subscription"}
              </p>
              <div className="rounded border bg-muted/40 px-3 py-2 whitespace-pre-wrap">
                {preview?.withSub || dict["No content"] || "—"}
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {dict["Message without subscription"] ||
                  "Message without subscription"}
              </p>
              <div className="rounded border bg-muted/40 px-3 py-2 whitespace-pre-wrap">
                {preview?.withoutSub || dict["No content"] || "—"}
              </div>
            </div>
          </div>
        </Card>
      </Modal>

      <AlertModal
        messageAlert={dict["Are you sure you want to delete this template?"]}
        open={openDeleteConfirm}
        onOpenChange={setOpenDeleteConfirm}
        confirm={confirmDelete}
        cancel={() => setOpenDeleteConfirm(false)}
      />
    </div>
  );
};

export default NotificationsInactiveManager;
